import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { X, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../../common/axiosInstance";
import { useS3Upload } from "../../../utils/s3Client";
import { DesignDocument } from "./DesignDocumentationIntegration";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { usePremisesStore } from "../../../store/store";

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingDocument?: DesignDocument | null;
  folderId?: string;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  existingDocument = null,
  folderId,
}) => {
  const [formData, setFormData] = useState({
    documentName: "",
    documentType: "architectural_drawing" as "architectural_drawing" | "blueprint" | "permit" | "other",
    description: "",
    region: "",
    premises: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [changeDescription, setChangeDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { uploadFileToS3, progress } = useS3Upload();
  const { fetchPremises } = usePremisesStore();

  const resetForm = useCallback(() => {
    setFormData({
      documentName: "",
      documentType: "architectural_drawing",
      description: "",
      region: "",
      premises: "",
    });
    setFile(null);
    setChangeDescription("");
    setErrors({});
  }, []);

  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  useEffect(() => {
    if (isOpen) {
      if (existingDocument) {
        setFormData({
          documentName: existingDocument.documentName,
          documentType: existingDocument.documentType,
          description: existingDocument.description || "",
          region: existingDocument.region || "",
          premises: existingDocument.premises || "",
        });
      } else {
        resetForm();
      }
    }
  }, [existingDocument, isOpen, resetForm]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.documentName.trim()) {
      newErrors.documentName = "Document name is required";
    }

    if (!file && !existingDocument) {
      newErrors.file = "Please select a file to upload";
    }

    if (existingDocument && !changeDescription.trim()) {
      newErrors.changeDescription = "Change description is required for new version";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      
      // Validate file type by extension for small files that might have incorrect MIME types
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar', 'txt'];
      
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
        "text/plain", // For txt files
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/zip",
        "application/x-rar-compressed",
      ];

      // Check by MIME type first, then by extension (for small files with incorrect MIME types)
      const isValidType = allowedTypes.includes(selectedFile.type) || allowedExtensions.includes(fileExtension);
      
      if (!isValidType) {
        toast.error("File type not supported. Please upload PDF, images, or Office documents.");
        return;
      }

      // Validate file size (max 50MB, no minimum - even 0 bytes or 1KB files are allowed)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (selectedFile.size > maxSize) {
        toast.error("File size exceeds 50MB limit");
        return;
      }

      // Set file immediately - even for small files (1KB, etc.)
      setFile(selectedFile);
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "text/plain": [".txt"], // Support text files (often small like 1KB)
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
      "application/zip": [".zip"],
      "application/x-rar-compressed": [".rar"],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    minSize: 0, // Allow even 0-byte files (explicitly allow small files like 1KB)
    multiple: false,
    noClick: false,
    noKeyboard: false,
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    if (bytes < k) {
      return bytes + " Byte" + (bytes !== 1 ? "s" : "");
    }
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setUploading(true);

    try {
      let fileUrl = "";
      let fileName = "";

      if (file) {
        // Upload file to S3
        fileUrl = await uploadFileToS3(file);
        fileName = file.name;
      } else if (existingDocument) {
        // Use existing file for version update
        fileUrl = existingDocument.fileUrl;
        fileName = existingDocument.fileName;
      }

      if (existingDocument) {
        // Create new version
        const docId = existingDocument._id || existingDocument.id;
        const docFolderId = folderId || (existingDocument as any).folderId;
        
        if (!docId || !docFolderId) {
          throw new Error("Document ID or Folder ID not found");
        }

        const versionData = {
          fileUrl: fileUrl,
          fileName: fileName,
          uploadedAt: new Date().toISOString(),
          changeDescription: changeDescription,
          fileSize: file?.size || existingDocument.fileSize,
        };

        const response = await axiosInstance.post(
          `/api/utility-systems/design-folders/${docFolderId}/documents/${docId}/versions`,
          versionData
        );

        if (response.data.success && response.data.resCode === 200) {
          toast.success(response.data.resMessage || "New version uploaded successfully");
          resetForm();
          onSuccess();
          onClose();
        } else {
          throw new Error(response.data.resMessage || "Failed to upload version");
        }
      } else {
        // Create new document
        if (!folderId) {
          throw new Error("Folder ID is required");
        }

        const documentData = {
          documentName: formData.documentName,
          documentType: formData.documentType,
          description: formData.description,
          fileUrl: fileUrl,
          fileName: fileName,
          fileSize: file?.size || 0,
          uploadedAt: new Date().toISOString(),
          region: formData.region || undefined,
          premises: formData.premises || undefined,
          tags: [],
          status: "active",
        };

        const response = await axiosInstance.post(
          `/api/utility-systems/design-folders/${folderId}/documents`,
          documentData
        );

        if (response.data.success && response.data.resCode === 200) {
          toast.success(response.data.resMessage || "Document uploaded successfully");
          resetForm();
          onSuccess();
          onClose();
        } else {
          throw new Error(response.data.resMessage || "Failed to upload document");
        }
      }
    } catch (error: any) {
      console.error("Error uploading document:", error);
      const errorMessage = error.response?.data?.resMessage || error.response?.data?.message || "Failed to upload document. Please try again.";
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1000001]">
      <div className="bg-white rounded-lg shadow-lg w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="p-6 overflow-hidden">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">
              {existingDocument ? "Upload New Version" : "Upload Document"}
            </h2>
            <hr className="my-2 border-gray-300" />
          </div>

          {/* Document Name */}
          <div className="mb-4">
            <label className="block text-gray-600 font-semibold mb-2">
              Document Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="documentName"
              value={formData.documentName}
              onChange={handleInputChange}
              disabled={!!existingDocument}
              className={`w-full border rounded-md p-2 ${
                errors.documentName ? "border-red-500" : "border-gray-300"
              } ${existingDocument ? "bg-gray-100" : ""}`}
              placeholder="Enter document name"
            />
            {errors.documentName && (
              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.documentName}
              </p>
            )}
          </div>

          {/* Document Type, Region, Premises */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="w-full">
              <label className="block text-gray-600 font-semibold mb-2">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                name="documentType"
                value={formData.documentType}
                onChange={handleInputChange}
                disabled={!!existingDocument}
                className={`w-full border rounded-md p-2 ${
                  existingDocument ? "bg-gray-100" : ""
                }`}
              >
                <option value="architectural_drawing">Architectural Drawing</option>
                <option value="blueprint">Blueprint</option>
                <option value="permit">Permit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="w-full">
              <label className="block text-gray-600 font-semibold">
                Region
              </label>
              <Dropdown
                selectedValue={formData.region}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, region: value }));
                  setErrors((prev) => ({ ...prev, region: "" }));
                }}
                type="region"
              />
            </div>
            <div className="w-full">
              <label className="block text-gray-600 font-semibold">
                Premises
              </label>
              <Dropdown
                selectedValue={formData.premises}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, premises: value }));
                  setErrors((prev) => ({ ...prev, premises: "" }));
                }}
                type="premises"
                selectedRegion={formData.region}
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-gray-600 font-semibold mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder="Enter document description"
            />
          </div>

          {/* File Upload - Exactly like Policies and Manuals */}
          <div>
            <label className="font-semibold block text-gray-600 mb-2">
              {existingDocument ? "New Version File" : "Upload Files"} <span className="text-red-500">*</span>
            </label>
            <div
              {...getRootProps()}
              className={`mb-4 border-dashed border-2 p-6 text-center cursor-pointer ${
                file ? "border-green-500 bg-green-50" : isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <input {...getInputProps()} />
              {file ? (
                <div>
                  <p className="text-gray-600 font-medium text-base mb-1">{file.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                </div>
              ) : isDragActive ? (
                <p className="text-blue-600 font-medium">Drop the file here...</p>
              ) : (
                <p className="text-gray-600">Drag & Drop Your Files Here Or Click</p>
              )}
              {!file && (
                <p className="text-sm text-gray-500 mt-2">
                  Supported formats: PDF, Images, DOC, DOCX, XLS, XLSX, PPT, PPTX, ZIP, RAR (Max: 50MB)
                </p>
              )}
            </div>
            {errors.file && (
              <p className="text-red-600 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.file}
              </p>
            )}
          </div>

          {/* Change Description (for new versions) */}
          {existingDocument && (
            <div className="mb-4">
              <label className="block text-gray-600 font-semibold mb-2">
                Change Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={changeDescription}
                onChange={(e) => {
                  setChangeDescription(e.target.value);
                  setErrors((prev) => ({ ...prev, changeDescription: "" }));
                }}
                rows={3}
                className={`w-full border rounded-md p-2 ${
                  errors.changeDescription ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Describe the changes in this version"
              />
              {errors.changeDescription && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.changeDescription}
                </p>
              )}
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-700">Uploading...</p>
                <p className="text-sm font-semibold text-blue-600">{progress}%</p>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer - Exactly like Policies and Manuals */}
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 bg-gray-400 text-white rounded-full disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading}
            className="px-4 py-2 bg-teal-500 text-white rounded-full disabled:opacity-50"
          >
            {uploading ? "Uploading..." : existingDocument ? "Upload Version" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
